import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    List, ListItem, ListItemText, ListItemIcon, CircularProgress, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box,
    Typography, Paper, Card, CardContent, Grid, Link, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import BlockIcon from '@mui/icons-material/Block';
import Tooltip from '@mui/material/Tooltip';
import Swal from 'sweetalert';
import DrepturiUtilizator from '../utilizator/drepturiUtilizator';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';

import verificareToken from '../VerificareToken';
import { useDenInstante, statusInstantaDen, DenPortalIndicator, extrageCodInstantaDinNumarDosar } from '../den/denPortal';

// PDF servit direct de pe juristpro.ro ca sa evitam rutarea SPA (.htaccess trimitea
// caile fara fisier real catre index.html, deci iframe-ul afisa aplicatia neautentificata).
const DEN_PDF_URL = 'https://juristpro.ro/next/inregistrare_cont_den.pdf';

function DosarElectronic() {
    const { areDrept } = DrepturiUtilizator();
    const DreptDeModificareDosare = 'DreptDeModificareDosare';

    const { id } = useParams();
    const [documente, setDocumente] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [currentDoc, setCurrentDoc] = useState(null);
    const [formState, setFormState] = useState({ descriereFisier: '', dataFisier: '', fisier: null });
    const [isAddingOrEditing, setIsAddingOrEditing] = useState(false);
    const [openPdf, setOpenPdf] = useState(false);

    // Indicator DEN - lista instantelor de pe portal (incarcata o singura data)
    const instanteDen = useDenInstante();
    // Codul instantei se determina din NUMARUL UNIC al dosarului (segmentul din mijloc),
    // exact ca in sertarul DEN din Menu.js -> cele doua locuri sunt mereu consecvente.
    // Sursa principala: numarul dosarului adus din `dosare/:id` (merge si pe dosar GOL).
    // Fallback: nrDosar din documentele electronice, apoi idInstanta din stadiul curent.
    const [numarDosarUnic, setNumarDosarUnic] = useState('');
    const [idInstantaStadiu, setIdInstantaStadiu] = useState(null);

    const codInstantaDinNumar = extrageCodInstantaDinNumarDosar(
        numarDosarUnic || (documente.length > 0 ? documente[0].nrDosar : '')
    );
    const idInstantaDosar = codInstantaDinNumar != null ? codInstantaDinNumar : idInstantaStadiu;
    const statusDen = idInstantaDosar != null ? statusInstantaDen(instanteDen, idInstantaDosar) : 'necunoscut';

    const handleVizualizare = (doc) => {
        const urlLaravel = window.API_URL.replace('/api/', '');
        const fileSuffix = '/fisiere/in/';

        if (doc && doc.id) {
            const fileUrl = `${urlLaravel}${fileSuffix}${id}/electronic/${encodeURIComponent(doc.numeFisier)}`;
            window.open(fileUrl, '_blank');
        }
    };

    const apiUrl = window.API_URL;
    const { token } = verificareToken();

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${apiUrl}getDosareElectroniceForDosar/${id}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setDocumente(data.data.dosarElectronic);
        } catch (error) {
            console.error('A apărut o eroare!', error);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id, token, apiUrl]);

    // Sursa principala: numarul unic al dosarului din `dosare/:id` (merge si pe dosar gol).
    // Numarul se compune din numardosar/instantadosar/andosar; segmentul din mijloc = codul instantei.
    useEffect(() => {
        let activ = true;
        const fetchNumarDosar = async () => {
            try {
                const response = await fetch(`${apiUrl}dosare/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) return;
                const data = await response.json();
                const sumar = data && data.data ? data.data.sumarDosar : null;
                if (!activ || !sumar) return;
                if (sumar.numardosar != null && sumar.instantadosar != null && sumar.andosar != null) {
                    setNumarDosarUnic(`${sumar.numardosar}/${sumar.instantadosar}/${sumar.andosar}`);
                }
            } catch (error) {
                // esec silentios - nu afisam indicatorul, nu blocam pagina
            }
        };
        fetchNumarDosar();
        return () => {
            activ = false;
        };
    }, [id, token, apiUrl]);

    // Fallback (ultima solutie): idInstanta din stadiul curent, folosit doar daca numarul
    // unic al dosarului nu poate fi obtinut (nici din dosare/:id, nici din documente).
    useEffect(() => {
        let activ = true;
        const fetchInstantaStadiu = async () => {
            try {
                const response = await fetch(`${apiUrl}infoStadiiDosar/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (!response.ok) return;
                const data = await response.json();
                const stadii = data && data.data ? data.data.stadiiDosar : null;
                if (!activ || !Array.isArray(stadii) || stadii.length === 0) return;

                const stadiuCurent = stadii.find(s => s.id === data.data.stadiuCurent);
                let idInstanta = stadiuCurent ? stadiuCurent.idInstanta : null;
                if (idInstanta == null) {
                    const cuInstanta = [...stadii].reverse().find(s => s.idInstanta != null && s.idInstanta !== '');
                    idInstanta = cuInstanta ? cuInstanta.idInstanta : null;
                }
                if (activ && idInstanta != null && idInstanta !== '') {
                    setIdInstantaStadiu(parseInt(idInstanta, 10));
                }
            } catch (error) {
                // esec silentios
            }
        };
        fetchInstantaStadiu();
        return () => {
            activ = false;
        };
    }, [id, token, apiUrl]);

    const handleOpen = (doc) => {
        if (doc) {
            const dataDocumentAsDate = doc.dataFisier ? new Date(doc.dataFisier) : new Date();
            setFormState({ descriereFisier: doc.descriereFisier, dataFisier: dataDocumentAsDate, fisier: doc.numeFisier });
        } else {
            const currentDate = new Date();
            setFormState({ descriereFisier: '', dataFisier: currentDate, fisier: null });
        }
        setCurrentDoc(doc);
        setOpen(true);
    };


    const handleClose = () => {
        setCurrentDoc(null);
        setFormState({ descriereFisier: '', dataFisier: '', fisier: null });
        setOpen(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormState(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        // La adăugare (fără currentDoc) permitem fișiere multiple, la editare rămâne un singur fișier
        const selectie = currentDoc ? e.target.files[0] : Array.from(e.target.files);
        setFormState(prevState => ({
            ...prevState,
            fisier: selectie
        }));
    };

    function formatDate(dateString) {
        const dateParts = dateString.split('-');
        return `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
    }

const handleAdaugaSauEditeaza = async () => {
    setIsAddingOrEditing(true);
    const formattedDataDocument = formState.dataFisier instanceof Date
    ? `${formState.dataFisier.getFullYear()}-${(formState.dataFisier.getMonth() + 1).toString().padStart(2, '0')}-${formState.dataFisier.getDate().toString().padStart(2, '0')}`
    : formState.dataFisier;

    const formData = new FormData();
    formData.append('idDosar', id);
    formData.append('descriereFisier', formState.descriereFisier);
    formData.append('dataFisier', formattedDataDocument);
    if (currentDoc) {
        // Editare: un singur fișier (doar dacă este un File nou, nu numele existent string)
        if (formState.fisier instanceof File) {
            formData.append('fisier', formState.fisier);
        }
    } else if (Array.isArray(formState.fisier)) {
        // Adăugare: upload multiplu
        formState.fisier.forEach(f => formData.append('fisiere[]', f));
    } else if (formState.fisier) {
        formData.append('fisier', formState.fisier);
    }

    try {
        if (currentDoc) {
            const response = await fetch(`${apiUrl}updateDosarElectronic/${currentDoc.id}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });
            const data = await response.json();
            setDocumente(documente.map(doc => doc.id === currentDoc.id ? data : doc));
        } else {
            const response = await fetch(`${apiUrl}storeDosarElectronic`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });
            const data = await response.json();
            setDocumente([...documente, data]);
        }
        handleClose();
        fetchData();
    } catch (error) {
        console.error('A apărut o eroare!', error);
    } finally {
        setIsAddingOrEditing(false);
    }
};

const handleSterge = async (docId) => {
    setIsAddingOrEditing(true);

    Swal({
        title: 'Sigur doriți ștergerea?',
        text: 'Această acțiune nu poate fi anulată!',
        icon: "warning",
        buttons: true,
        dangerMode: true,
    })
    .then(async (willDelete) => {
        if (willDelete) {
            try {
                await fetch(`${apiUrl}deleteDosarElectronic/${docId}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setDocumente(documente.filter(doc => doc.id !== docId));
                Swal("Șters!", "Documentul a fost șters.", "success");
                fetchData();
            } catch (error) {
                console.error('A apărut o eroare la ștergere!', error);
            } finally {
                setIsAddingOrEditing(false);
            }
        } else {
            setIsAddingOrEditing(false);
        }
    });
};

    // Cardurile cu intrebari frecvente (reimplementare MUI a markup-ului Bulma original)
    const openPdfModal = () => setOpenPdf(true);

    const faqCards = [
        {
            icon: <EmailIcon fontSize="inherit" />,
            titlu: 'Nu primesc codul de autentificare pe e-mail!',
            corp: (
                <>
                    Aplicațiile anterioare trimiteau codul de autentificare inițial prin SMS, ulterior prin E-Mail.
                    Autentificarea în DEN se face cu codul generat de aplicația instalată în dispozitivul dumneavoastră mobil.
                    Pentru mai multe informații, vă rugăm{' '}
                    <Link component="button" type="button" onClick={openPdfModal} sx={{ verticalAlign: 'baseline' }}>
                        revedeți pagina 5 a documentului pdf
                    </Link>{' '}
                    din secțiunea de mai sus - Informații utile - înregistrare cont nou.
                </>
            ),
        },
        {
            icon: <LockIcon fontSize="inherit" />,
            titlu: 'Nu mă pot autentifica!',
            corp: 'Mai mult ca sigur ați introdus greșit datele de autentificare, verificați adresa de e-mail, parola sau codul QR. De asemenea vă rugăm verificați dacă ceasul dispozitivului mobil corespunde cu ceasul din formularul de autentificare. Codul este generat o dată la 30 secunde, în funcție de o cheie unică și ceasul universal, dacă timpul nu corespunde veți introduce un cod de autentificare greșit.',
        },
        {
            icon: <ReportProblemIcon fontSize="inherit" />,
            titlu: 'Toate datele sunt corecte, tot nu mă pot autentifica!',
            corp: 'În cazul în care ați lăsat în browser deschisă aplicația DEN, la re-deschiderea browser-ului web, este posibil ca pagina web să vă fie afișată din cache-ul browser-ului iar sesiunea să fi expirat. Vă rugăm ca înainte de completarea formularului de autentificare să re-încărcați pagina web sau dați un click pe link-ul Autentificare din meniul general al paginii web pentru re-încărcarea paginii.',
        },
        {
            icon: <PersonSearchIcon fontSize="inherit" />,
            titlu: 'Nu mă pot înregistra!',
            corp: 'Se pot înregistra în DEN, doar persoanele care au adresa de e-mail și numărul de telefon înregistrat în dosarul din care face parte. Aceste date ori le-ați furnizat în momentul în care s-a făcut dosarul, ori ați făcut o cerere de acces la dosarul electronic trimisă instanței sau la registratura instanței.',
        },
        {
            icon: <VpnKeyIcon fontSize="inherit" />,
            titlu: 'Am uitat parola, mi-am schimbat mobil-ul nu mai am codul QR!',
            corp: 'Formularul "resetare parolă / cod QR" din partea dreaptă jos a casetei de autentificare vă permite schimbarea parolei sau recuperarea codului QR. În cazul în care folosiți des aplicația DEN, puteți salva parola în browser sau într-un manager de parole, oricum, pentru autentificare veți avea nevoie codul de autentificare generat de dispozitivul mobil.',
        },
        {
            icon: <BlockIcon fontSize="inherit" />,
            titlu: 'Am fost blocat de aplicație, nu mă mai pot autentifica!',
            corp: 'Blocarea autentificării este una dintre metodele prin care aplicația se auto-protejează. Ne pare rău că ați fost blocat dar la un anumit număr de încercări greșite de autentificare ați fost avertizat de acest lucru. În cazul în care insistați, timpul de blocare crește. Blocarea se face doar dacă ați introdus greșit datele de autentificare, sau ați încercat alte metode de autentificare în afară de cele furnizate de aplicație.',
        },
    ];

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>;
    }

    return (
        <div>
        <div className='mb-3' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <Typography style={{fontWeight: 'bold', fontSize:'1.3em'}}>Dosar electronic</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* Indicator "disponibil pe portalul den.ro" (dreapta sus) */}
                <DenPortalIndicator status={statusDen} />
                {areDrept(DreptDeModificareDosare) ? (
                    <Button
                    disabled={!areDrept(DreptDeModificareDosare)}
                    onClick={() => handleOpen(null)}
                    variant="contained"
                    size="small"
                    color="secondary"
                    className='pull-right'
                    startIcon={<AddIcon />}
                >
                    Adaugare document nou
                </Button>
                ) : null }
            </Box>
        </div>
        {documente.length === 0 ? (
            // Stare goala: butoane de actiune + intrebari frecvente
            <Box>
                <Paper
                    elevation={0}
                    sx={{
                        textAlign: 'center',
                        py: 5,
                        px: 3,
                        mb: 4,
                        borderRadius: 2,
                        border: '1px dashed rgba(var(--background-color), 0.4)',
                        background: 'linear-gradient(135deg, rgba(var(--background-color), 0.05) 0%, rgba(var(--background-color), 0.02) 100%)',
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 56, color: 'rgba(var(--background-color), 1)', mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Nu există documente atașate dosarului electronic
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Încărcați primul document sau consultați procedura de înregistrare în Dosarul Electronic Național.
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="center"
                        alignItems="center"
                    >
                        {areDrept(DreptDeModificareDosare) ? (
                            <Button
                                onClick={() => handleOpen(null)}
                                variant="contained"
                                color="secondary"
                                startIcon={<CloudUploadIcon />}
                            >
                                Încărcare document
                            </Button>
                        ) : null}
                        <Button
                            onClick={openPdfModal}
                            variant="outlined"
                            color="secondary"
                            startIcon={<MenuBookIcon />}
                        >
                            Procedura de înregistrare în Dosarul Electronic Național
                        </Button>
                    </Stack>
                </Paper>

                {/* Intrebari frecvente */}
                <Typography
                    variant="h5"
                    align="center"
                    sx={{ fontWeight: 600, mb: 3, color: 'rgba(var(--background-color), 1)' }}
                >
                    Întrebări frecvente
                </Typography>
                <Grid container spacing={3}>
                    {faqCards.map((card, idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <Card
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px) scale(1.02)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                    },
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ textAlign: 'center', color: 'rgba(var(--background-color), 1)', fontSize: '2rem', mb: 1 }}>
                                        {card.icon}
                                    </Box>
                                    <Typography variant="h6" align="center" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1.5 }}>
                                        {card.titlu}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'justify' }}>
                                        {card.corp}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <List disablePadding>
                    {documente.map((doc, index) => (
                        <ListItem
                            key={doc.id}
                            divider={index < documente.length - 1}
                            className={index % 2 === 0 ? 'list-item striped' : 'list-item'}
                            sx={{
                                transition: 'background-color 0.15s ease',
                                '&:hover': { backgroundColor: 'rgba(var(--background-color), 0.06)' },
                            }}
                            secondaryAction={
                                <Box sx={{ display: 'flex' }}>
                                    <Tooltip title="Vizualizare">
                                        <IconButton aria-label="vizualizare" onClick={() => handleVizualizare(doc)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    {areDrept(DreptDeModificareDosare) ? (
                                        <>
                                            <Tooltip title="Editare">
                                                <IconButton disabled={!areDrept(DreptDeModificareDosare)} aria-label="editeaza" onClick={() => handleOpen(doc)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Ștergere">
                                                <IconButton disabled={!areDrept(DreptDeModificareDosare)} aria-label="sterge" onClick={() => handleSterge(doc.id)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : null}
                                </Box>
                            }
                        >
                            <ListItemIcon sx={{ minWidth: 42, color: 'rgba(var(--background-color), 1)' }}>
                                <InsertDriveFileIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={doc.numeFisier}
                                primaryTypographyProps={{ style: { fontWeight: 600, paddingRight: 120 } }}
                                secondary={[
                                    doc.dataFisier ? `Data: ${formatDate(doc.dataFisier)}` : '',
                                    doc.descriereFisier ? `Descriere: ${doc.descriereFisier}` : '',
                                ].filter(Boolean).join('\n')}
                                secondaryTypographyProps={{ style: { whiteSpace: 'pre-line', paddingRight: 120 } }}
                            />
                        </ListItem>
                    ))}
                </List>
            </Paper>
        )}

            {/* Modala cu procedura PDF de inregistrare in Dosarul Electronic National */}
            <Dialog open={openPdf} onClose={() => setOpenPdf(false)} fullWidth maxWidth="lg">
                <DialogTitle className='titluModal' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span><MenuBookIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> Procedura de înregistrare în Dosarul Electronic Național</span>
                    <IconButton aria-label="inchidere" onClick={() => setOpenPdf(false)} sx={{ color: 'inherit' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <iframe
                        src={DEN_PDF_URL}
                        title="Procedura de înregistrare DEN"
                        style={{ width: '100%', height: '80vh', border: 'none' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        component="a"
                        href={DEN_PDF_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        color="secondary"
                    >
                        Deschide în filă nouă
                    </Button>
                    <Button onClick={() => setOpenPdf(false)} color="error">
                        Închidere
                    </Button>
                </DialogActions>
            </Dialog>

            {/* dialog  de adaugare si editare */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" aria-labelledby="form-dialog-title">
                <DialogTitle  className='titluModal' id="form-dialog-title">{currentDoc ? 'Editare document' : 'Adăugare document'}</DialogTitle>
                <DialogContent className='mt-3'>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="descriereFisier"
                        label="Descriere"
                        type="text"
                        fullWidth
                        value={formState.descriereFisier}
                        onChange={handleInputChange}
                        name="descriereFisier"
                        required
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
                            <DatePicker
                                label="Data Document"
                                value={formState.dataFisier}
                                onChange={(newValue) => {
                                    setFormState(prevState => ({ ...prevState, dataFisier: newValue }));
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                                inputFormat="dd/MM/yyyy"
                                format="dd.MM.yyyy"
                                mask="__.__.____"
                                className='mt-2 maxWidth mb-3'
                            />
                        </LocalizationProvider>

                    <Box
                        sx={{
                            mt: 2,
                            mb: 1,
                            p: 3,
                            textAlign: 'center',
                            border: '2px dashed rgba(var(--background-color), 0.4)',
                            borderRadius: 2,
                            backgroundColor: 'rgba(var(--background-color), 0.03)',
                        }}
                    >
                        <input
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.ppt,.pptx,.mp3,.wav,.mp4,.avi,.mov,.csv"
                            style={{ display: 'none' }}
                            id="contained-button-file"
                            multiple={!currentDoc}
                            type="file"
                            onChange={handleFileChange}
                        />
                        <CloudUploadIcon sx={{ fontSize: 40, color: 'rgba(var(--background-color), 1)', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            {currentDoc
                                ? 'Selectați un fișier pentru a înlocui documentul curent.'
                                : 'Selectați unul sau mai multe fișiere pentru încărcare.'}
                        </Typography>
                        <label htmlFor="contained-button-file">
                            <Button variant="outlined" component="span" size='small' startIcon={<CloudUploadIcon />}>
                                {currentDoc ? 'Alege fișier' : 'Alege fișiere'}
                            </Button>
                        </label>

                        {Array.isArray(formState.fisier) ? (
                            formState.fisier.length > 0 && (
                                <List dense sx={{ mt: 1, textAlign: 'left' }}>
                                    {formState.fisier.map((f, idx) => (
                                        <ListItem key={idx} disableGutters>
                                            <ListItemIcon sx={{ minWidth: 30 }}>
                                                <InsertDriveFileIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={f.name}
                                                primaryTypographyProps={{ variant: 'body2', color: 'textSecondary' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )
                        ) : (
                            formState.fisier && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Numele documentului: {formState.fisier.name || formState.fisier}
                                </Typography>
                            )
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAdaugaSauEditeaza}
                        color="success"
                        disabled={isAddingOrEditing || !formState.descriereFisier || formState.descriereFisier.trim() === '' || (Array.isArray(formState.fisier) ? formState.fisier.length === 0 : (!formState.fisier && !formState.numeFisier))}
                        >
                        {isAddingOrEditing ? 'Se procesează...' : (currentDoc ? 'Salvare' : 'Adăugare')}
                    </Button>

                    <Button onClick={handleClose} color="error">
                        Anulare
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default DosarElectronic;
